import React, { useEffect, useRef, useState } from "react"
import { Box, Stack, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { faHandshake, faRightFromBracket } from "@fortawesome/pro-light-svg-icons"
import { isEmpty } from "lodash"
import { enqueueSnackbar } from "notistack"

import { AttendeeState, MeetingAttendee } from "../../meetings/meetingTypes"
import { ServiceCenterMeetingQuery } from "../../meetings/meetingApi"
import { checkOutGuest } from "../../serviceCenterHomeApi"
import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import { useCurrentServiceCenter } from "../../../../store/serviceCenterHooks"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { ActionBarItem } from "../../../../../../shared/components/table/FloatingActionBar"
import { GuestData } from "../../../external/brotherPrinter"

import { GuestsList } from "./GuestsList"
import { ActionBarItemPrintGuestLabels } from "./ActionBarItemPrintGuestLabels"
import { PrintLabelsModal } from "../checkIn/PrintLabelsModal"
import { GuestCheckInModal } from "../checkIn/GuestCheckInModal"
import { ActionBarDrawer } from "./ActionBarDrawer"

const useStyles = makeStyles()(() => ({
    container: {
        display: "flex",
        flexDirection: "column",
    },
    header: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: ".5rem",
    },
}))

type GuestsSectionProps = {
    guests: MeetingAttendee[] | undefined
    hostId?: number
}

type GuestListSections = "checkedIn" | "notCheckedIn"

export function GuestsSection({ guests, hostId }: GuestsSectionProps) {
    const { l10n } = useAppLocalization()
    const { classes } = useStyles()
    const [selectedNotCheckedIds, setSelectedNotCheckedIds] = useState<number[]>([])
    const [selectedCheckedIds, setSelectedCheckedIds] = useState<number[]>([])
    const [printLabelModal, setPrintLabelModal] = useState(false)
    const [printLabelData, setPrintLabelData] = useState<Array<GuestData>>()
    const [checkInModal, setCheckInModal] = useState(false)
    const [checkInData, setCheckInData] = useState<Array<MeetingAttendee>>()
    const [expanded, setExpanded] = React.useState<GuestListSections | false>("notCheckedIn")

    const handleChange = (panel: GuestListSections) => (event: React.SyntheticEvent, isExpanded: boolean) => {
        setExpanded(isExpanded ? panel : false)
    }

    function toggleSelection(setterFunction: React.Dispatch<React.SetStateAction<number[]>>, id: number): void {
        setterFunction((prevState) => {
            if (prevState.includes(id)) {
                return prevState.filter((selectedId) => selectedId !== id)
            } else {
                return [...prevState, id]
            }
        })
    }

    const handleSelectNotChecked = (id: number) => {
        toggleSelection(setSelectedNotCheckedIds, id)
    }

    const handleSelectChecked = (id: number) => {
        toggleSelection(setSelectedCheckedIds, id)
    }

    const handleClearSelected = () => {
        setSelectedNotCheckedIds([])
        setSelectedCheckedIds([])
    }

    const { serviceCenterId } = useCurrentServiceCenter()
    const [checkOutSucceeded, setCheckOutSucceeded] = useState(false)
    const successTimeoutRef = useRef<NodeJS.Timeout | null>(null)

    useEffect(() => {
        return () => {
            if (successTimeoutRef.current) {
                clearTimeout(successTimeoutRef.current)
            }
        }
    }, [])

    const { mutate: checkOutGuests, isLoading: isCheckingOut } = useApiMutation(checkOutGuest, {
        async onSuccess(_params, _data, context) {
            await context.queryClient.invalidateQueries({
                queryKey: [ServiceCenterMeetingQuery.SERVICE_CENTER_MEETING_DETAILS],
            })
            enqueueSnackbar({
                variant: "success",
                message: l10n.getString("service-center-reports-dashboard-meetings-drawer-check-out-success"),
            })
            setCheckOutSucceeded(true)
            successTimeoutRef.current = setTimeout(() => {
                setCheckOutSucceeded(false)
                handleClearSelected()
            }, 1200)
        },
    })

    const handleCheckOut = (attendeeIds: number[]) => {
        if (isCheckingOut || checkOutSucceeded || isEmpty(attendeeIds)) {
            return
        }

        checkOutGuests({ serviceCenterId, attendees: attendeeIds })
    }

    const checkedInGuests = guests?.filter((person) => person.state === AttendeeState.CHECKED_IN)
    const notCheckedInGuests = guests?.filter((person) => person.state === AttendeeState.EXPECTED)

    const hasNotCheckedSelections = !isEmpty(selectedNotCheckedIds)
    const hasCheckedSelections = !isEmpty(selectedCheckedIds)

    const notCheckedInIds = notCheckedInGuests?.map((guest) => guest.id) ?? []
    const checkedInIds = checkedInGuests?.map((guest) => guest.id) ?? []

    const allNotCheckedSelected =
        notCheckedInIds.length > 0 && notCheckedInIds.every((id) => selectedNotCheckedIds.includes(id))
    const allCheckedSelected = checkedInIds.length > 0 && checkedInIds.every((id) => selectedCheckedIds.includes(id))

    const toggleSelectAllNotChecked = () => setSelectedNotCheckedIds(allNotCheckedSelected ? [] : notCheckedInIds)
    const toggleSelectAllChecked = () => setSelectedCheckedIds(allCheckedSelected ? [] : checkedInIds)

    return (
        <Box className={classes.container}>
            <Box className={classes.header}>
                <LocalizedStrict id="service-center-reports-dashboard-meetings-drawer-guests-title">
                    <Typography variant="h5" fontWeight={500}>
                        Guests
                    </Typography>
                </LocalizedStrict>
            </Box>
            <Stack direction="column" spacing="2rem">
                <LocalizedStrict
                    id="service-center-reports-dashboard-meetings-drawer-guests-list-not-checked-title"
                    attrs={{ title: true }}
                >
                    <GuestsList
                        title="Not checked in"
                        guests={notCheckedInGuests}
                        selectedIds={selectedNotCheckedIds}
                        onSelectGuest={handleSelectNotChecked}
                        onToggleSelectAll={toggleSelectAllNotChecked}
                        allSelected={allNotCheckedSelected}
                        someSelected={hasNotCheckedSelections}
                        handleChange={handleChange("notCheckedIn")}
                        expanded={expanded === "notCheckedIn" || hasNotCheckedSelections}
                    />
                </LocalizedStrict>
                <LocalizedStrict
                    id="service-center-reports-dashboard-meetings-drawer-guests-list-checked-title"
                    attrs={{ title: true }}
                >
                    <GuestsList
                        title="Checked in"
                        guests={checkedInGuests}
                        selectedIds={selectedCheckedIds}
                        onSelectGuest={handleSelectChecked}
                        onToggleSelectAll={toggleSelectAllChecked}
                        allSelected={allCheckedSelected}
                        someSelected={hasCheckedSelections}
                        handleChange={handleChange("checkedIn")}
                        expanded={expanded === "checkedIn" || hasCheckedSelections}
                    />
                </LocalizedStrict>

                {(!isEmpty(selectedNotCheckedIds) || !isEmpty(selectedCheckedIds)) && (
                    <ActionBarDrawer
                        selectedItemsLength={selectedNotCheckedIds.length + selectedCheckedIds.length}
                        handleCloseActionBar={handleClearSelected}
                    >
                        {isEmpty(selectedCheckedIds) && (
                            <LocalizedStrict
                                id="service-center-reports-dashboard-meetings-drawer-guests-list-floating-check-action"
                                attrs={{ title: true }}
                            >
                                <ActionBarItem
                                    icon={faHandshake}
                                    title="Check in"
                                    onClick={() => {
                                        setCheckInModal(true)
                                        setCheckInData(
                                            notCheckedInGuests?.filter((guest) =>
                                                selectedNotCheckedIds.includes(guest.id),
                                            ),
                                        )
                                    }}
                                />
                            </LocalizedStrict>
                        )}
                        {isEmpty(selectedNotCheckedIds) && (
                            <LocalizedStrict
                                id={
                                    checkOutSucceeded
                                        ? "service-center-reports-dashboard-meetings-drawer-check-out-done"
                                        : "service-center-reports-dashboard-meetings-drawer-guests-list-floating-check-out-action"
                                }
                                attrs={{ title: true }}
                            >
                                <ActionBarItem
                                    icon={faRightFromBracket}
                                    title="Check out"
                                    isLoading={isCheckingOut}
                                    isSuccess={checkOutSucceeded}
                                    onClick={() => handleCheckOut(selectedCheckedIds)}
                                />
                            </LocalizedStrict>
                        )}
                        <ActionBarItemPrintGuestLabels
                            onClick={() => {
                                setPrintLabelModal(true)
                                setPrintLabelData(
                                    guests
                                        ?.filter(
                                            (guest) =>
                                                selectedCheckedIds.includes(guest.id) ||
                                                selectedNotCheckedIds.includes(guest.id),
                                        )
                                        .map((guest) => ({
                                            visitorName: guest.name,
                                            hostName: guest.companyName,
                                            visitorCompany: guest.companyName,
                                        })) || [],
                                )
                            }}
                        />
                    </ActionBarDrawer>
                )}
            </Stack>
            {printLabelModal && printLabelData?.length && (
                <PrintLabelsModal
                    labels={printLabelData}
                    open={printLabelModal}
                    onClose={() => {
                        setPrintLabelModal(false)
                    }}
                />
            )}

            {checkInModal && checkInData?.length && (
                <GuestCheckInModal
                    guests={checkInData}
                    hostId={hostId}
                    open={checkInModal}
                    onClose={() => {
                        setCheckInModal(false)
                    }}
                />
            )}
        </Box>
    )
}
